import { useCallback, useEffect, useRef, useState } from 'react'
import { deleteUser, getUserEmailUniqueness } from '@user/store/users/api'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { toast } from '@sales-center/components'
import { getLicenseInfo } from '@user/store/licensing/api'

export const useDeleteUser = (onSuccess: () => void) => {
  const dispatch = useAppDispatch()
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'fail'>(
    'idle',
  )

  const [toDelete, setToDelete] = useState<string>()

  const execute = useCallback(
    (id: string) => {
      if (status != 'pending') {
        setToDelete(id)
      }
    },
    [status],
  )

  const getUsers = async () => {
    try {
      const result = await dispatch(getUserEmailUniqueness())

      if (getUserEmailUniqueness.fulfilled.match(result)) {
        setStatus('success')
        toast.success('User deleted')
        dispatch(getLicenseInfo())
        onSuccessRef.current()
      } else {
        setStatus('fail')
        toast.error(`Error loading user list`)
      }
    } catch (error) {
      setStatus('fail')
    }
  }

  useEffect(() => {
    let mounted = true

    const execute = async () => {
      if (toDelete) {
        try {
          setStatus('pending')
          const result = await dispatch(deleteUser(toDelete))

          if (mounted) {
            if (deleteUser.fulfilled.match(result)) {
              await getUsers()
            } else {
              setStatus('fail')
              toast.error('Unable to delete user')
            }
          }
        } catch (error) {
          if (mounted) {
            setStatus('fail')
            toast.error(error.Message)
          }
        } finally {
          setToDelete(undefined)
          setStatus('idle')
        }
      }
    }
    execute()

    return () => {
      mounted = false
    }
  }, [dispatch, toDelete])

  return { status, execute }
}
